import React from "react";
import { useTranslation } from "react-i18next";
import { SubPage, FullDivider } from "./SubPage";
import { navigate, OVERLAY_ONLY } from "../App";
import { ProductAnchor } from "../components/ProductAnchor";

const BRAND = "#FF3C00";

const CORE = [
  { key: "overlay", href: "/products/ai-overlay" },
  { key: "rework", href: "/products/website-rework" },
  { key: "longterm", href: "/consulting" },
] as const;

const PRODUCT_META: Record<string, { tech: string[]; logo?: string; invertLogo?: boolean }> = {
  "one-click-deploy": { tech: ["CLI", "React", "Bun", "Docker"], logo: "/assets/one-click-deploy-logo.svg" },
  cate: { tech: ["Electron", "React", "Monaco", "xterm.js"], logo: "/assets/cate-logo.svg" },
  sight: { tech: ["Bun", "React", "PostgreSQL"] },
  skyline: { tech: ["iOS"] },
  foody: { tech: ["iOS", "Hono", "PostgreSQL"] },
};

export function Products() {
  const { t } = useTranslation("products");
  const { t: th } = useTranslation("home");
  const { t: tc } = useTranslation("common");
  const list = t("list", { returnObjects: true }) as { slug: string; name: string; tagline: string; description: string; status: string }[];
  const statusLabels = t("status", { returnObjects: true }) as Record<string, string>;

  // Website rework isn't offered when the AI_OVERLAY_ONLY gate is on.
  const core = OVERLAY_ONLY ? CORE.filter((c) => c.key !== "rework") : CORE;
  const coreColsClass = core.length === 2 ? "lg:grid-cols-2" : "lg:grid-cols-3";

  return (
    <SubPage title={t("title")} subtitle={t("subtitle")}>
      <section className="px-5 sm:px-8 md:px-[66px] pt-12 md:pt-16 pb-6">
        <h2 className="text-xl md:text-2xl font-bold text-white tracking-tight mb-2">{t("coreHeading")}</h2>
        <p className="text-sm text-grey-400 max-w-2xl leading-relaxed">{t("coreLead")}</p>
      </section>

      <div className="h-px bg-grey-800" />
      <div className={`grid grid-cols-1 ${coreColsClass}`}>
        {core.map((c, i) => {
          const s = th(`products.services.${c.key}`, { returnObjects: true }) as { name: string; tagline: string; desc: string; bullets: string[] };
          return (
            <a
              key={c.key}
              href={c.href}
              onClick={(e) => { e.preventDefault(); navigate(c.href); }}
              className="group flex flex-col p-6 lg:p-8
                         border-t border-grey-800 first:border-t-0
                         lg:border-t-0 lg:border-l lg:first:border-l-0 lg:border-grey-800
                         transition-colors hover:bg-[rgba(255,60,0,0.06)]"
            >
              <div className="text-sm font-mono text-grey-500 mb-4">{String(i + 1).padStart(2, "0")}</div>
              <h3 className="text-2xl font-bold text-white tracking-tight mb-2 leading-tight">{s.name}</h3>
              <p className="text-[14px] text-grey-400 leading-snug mb-5">{s.tagline}</p>
              <p className="text-[14px] text-grey-300 leading-relaxed mb-6">{s.desc}</p>
              <ul className="flex flex-col gap-1.5 mb-6">
                {s.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-[13px] text-grey-400 leading-snug">
                    <span className="font-bold mt-0.5 shrink-0 text-xs" style={{ color: BRAND }}>✓</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <span className="mt-auto inline-flex items-center gap-1.5 text-sm text-grey-300 group-hover:text-white transition-colors">
                {tc("learnMore")} <span>→</span>
              </span>
            </a>
          );
        })}
      </div>
      <div className="h-px bg-grey-800" />

      <section className="px-5 sm:px-8 md:px-[66px] pt-16 md:pt-20 pb-6">
        <h2 className="text-xl md:text-2xl font-bold text-white tracking-tight mb-2">{t("ownHeading")}</h2>
        <p className="text-sm text-grey-400 max-w-2xl leading-relaxed">{t("ownLead")}</p>
      </section>

      <div className="grid grid-cols-1 gap-px bg-grey-800">
        {list.map((product) => {
          const meta = PRODUCT_META[product.slug] ?? { tech: [] };
          return (
            <ProductAnchor
              key={product.slug}
              slug={product.slug}
              className="bg-grey-975 px-5 sm:px-8 md:px-[66px] py-8 md:py-10 group transition-colors hover:bg-white/[0.02]"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  {meta.logo ? (
                    <img src={meta.logo} alt={product.name} className={`h-5 w-auto object-contain${meta.invertLogo ? " invert" : ""}`} />
                  ) : (
                    <h3 className="text-base font-semibold text-white">{product.name}</h3>
                  )}
                  <span className="text-[9px] uppercase tracking-widest text-white/30 font-medium px-2 py-0.5 bg-white/[0.04]">
                    {statusLabels[product.status] ?? product.status}
                  </span>
                </div>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-white/20 group-hover:text-white/50 transition-colors">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </div>
              <p className="text-sm text-white/60 mb-1">{product.tagline}</p>
              <p className="text-xs text-white/30 leading-relaxed mb-4">{product.description}</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {meta.tech.map((tt) => (
                    <span key={tt} className="text-[9px] text-white/20 font-mono px-1.5 py-0.5 bg-white/[0.03]">{tt}</span>
                  ))}
                </div>
              </div>
            </ProductAnchor>
          );
        })}
      </div>
      <FullDivider />
    </SubPage>
  );
}
